import { compactFlatten } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import {
  getAreaIfDefined,
  getOtherTaskTemplate,
  getScanWithUpdatedGroup,
  getScopeForScanPartChildren,
  getTasksInStandardSortOrder,
  getTotalCostIfDefined,
  initTaskFromTemplate,
  ScanGroupOptimized,
  ScanTaskOptimized,
  ScanValue,
} from '@kaiizen/kai-scan-template-system';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useScanEditingContextSlice, useScanPartNotes } from '../../State';

import { formatUsd, groupNamePresenter } from '@/Client/Presenters';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { AddButton, Button, LabeledInput, LabeledInputStyle, ScanEditorPageMeasurementTitleBar, ScanEditorPageTitleBar } from '../../Widgets';
import { ClusteredTasksEditor } from './ClusteredTasksEditor';
import { PhotoColumn } from './PhotoColumn';
import { RoomMeasurementsModal } from './RoomMeasurementsModal';
import { ScanPartNotesButton } from './ScanPartNotesButton';
import { ScanValueView } from './ScanValueView';

const TASK_LIST_BOTTOM_SPACING_STYLE = css`
  label: TASK_LIST_BOTTOM_SPACING_STYLE;
  height: ${LAYOUT2.spacing * 10}px;
`;

const GROUP_EDITOR_STYLE = css`
  label: GROUP_EDITOR_STYLE;
  display: flex;
  flex-direction: row;
  gap: ${LAYOUT2.spacing2}px;
  height: 100%;
  background: ${COLORS2.alwaysDark.background.dark};
`;

const GROUP_STYLE = css`
  label: groupEditor;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background: var(--background2);
  position: relative;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
`;

const SCROLLABLE_CONTENT_STYLE = css`
  label: SCROLLABLE_CONTENT_STYLE;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  scroll-padding-top: 60px;
`;

const FIXED_HEIGHT_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const THREE_COLUMN_LAYOUT_STYLE = css`
  label: THREE_COLUMN_LAYOUT_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
`;

const GROUP_HEADER_CARD_COLUMN_STYLE = css`
  label: GROUP_HEADER_CARD_COLUMN_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1}px;
  ${TYPOGRAPHY2.xSmallBold}
  color: var(--text2);
`;

const GROUP_HEADER_CARD_COLUMN_LABEL_STYLE = css`
  label: GROUP_HEADER_CARD_COLUMN_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmallBold}
  color: var(--text2);
`;

const GROUP_HEADER_CARD_SUB_COLUMNS_STYLE = css`
  label: GROUP_HEADER_CARD_SUB_COLUMNS_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
`;

const GROUP_HEADER_CARD_SUB_COLUMN_STYLE = css`
  label: GROUP_HEADER_CARD_SUB_COLUMN_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingHalf}px;
`;

const TASKS_SECTION_STYLE = css`
  label: TASKS_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  background: var(--background2);
  flex: 1;
`;

const TASKS_HEADER_ADD_ONLY_CONTAINER_STYLE = css`
  label: TASKS_HEADER_ADD_ONLY_CONTAINER_STYLE;
  position: sticky;
  top: 0;
  z-index: 20;
`;

const TASKS_HEADER_ADD_ONLY_ROW_STYLE = css`
  label: TASKS_HEADER_ADD_ONLY_ROW_STYLE;
  display: flex;
  height: ${LAYOUT2.spacing5}px;
  align-items: center;
  justify-content: flex-end;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0 ${LAYOUT2.spacing1}px 0 ${LAYOUT2.spacing2}px;
  background: ${COLORS2.alwaysDark.background.dark};
`;

const ConditionalScanValueView = ({
  disabled: scanValueDisabled,
  label,
  valueKey,
  style,
  group,
  updateValue,
  tabIndex,
}: {
  disabled?: boolean;
  label?: string;
  valueKey: string;
  style?: LabeledInputStyle;
  group: ScanGroupOptimized;
  updateValue: (valueKey: string, value: ScanValue) => void;
  tabIndex?: number;
}) => {
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  const templateValue = group.template.values?.[valueKey];
  if (!templateValue) return <></>;
  const wrappedUpdateValue = (key: string, value: ScanValue) => {
    if (!scanIsEditable || scanValueDisabled) return;
    updateValue(key, value);
  };
  return (
    <ScanValueView
      width='140px'
      size='medium'
      valueKey={valueKey}
      value={group.values[valueKey] || {}}
      templateValue={templateValue}
      updateValue={wrappedUpdateValue}
      style={style}
      customLabel={label}
      tabIndex={tabIndex}
      disabled={scanValueDisabled}
    />
  );
};

const GROUP_HEADER_CARD_CONTENT_STYLE = css`
  label: GROUP_HEADER_CARD_CONTENT_STYLE;
  background: var(--background);
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  padding: ${LAYOUT2.spacing2}px;
`;

const ROOM_DIMENSION_VALUE_KEYS = ['roomLength', 'roomWidth', 'roomHeight'] as const;

const ROOM_DERIVED_AGGREGATE_VALUE_KEYS = ['roomArea', 'roomPerimeter', 'roomWallArea'] as const;

/**
 * Modal save pins totals via `setValue` on derived keys; expression eval skips those slots until overrides clear.
 */
const mergeClearDerivedRoomAggregateOverrides = (
  values: Record<string, ScanValue>,
  groupValues: ScanGroupOptimized['values'],
  editedKey: string,
) => {
  if (!(ROOM_DIMENSION_VALUE_KEYS as readonly string[]).includes(editedKey)) return;
  for (const derivedKey of ROOM_DERIVED_AGGREGATE_VALUE_KEYS) {
    values[derivedKey] = { ...(groupValues?.[derivedKey] ?? {}), setValue: null };
  }
};

const GroupRoomValuesEditor = ({
  group,
}: {
  group: ScanGroupOptimized;
}) => {
  const { scanDataMutators } = useScanEditingContextSlice(['scanDataMutators']);
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);
  const isRoom = !!group.template.values?.roomArea;
  const updateValue = (valueKey: string, value: ScanValue) => {
    if (!scanIsEditable) return;
    const values: Record<string, ScanValue> = { [valueKey]: value };
    mergeClearDerivedRoomAggregateOverrides(values, group.values, valueKey);
    scanDataMutators.updateGroup(group.id, { values });
  };
  return (
    <div css={GROUP_HEADER_CARD_CONTENT_STYLE}>
      <div css={THREE_COLUMN_LAYOUT_STYLE}>
        <div css={GROUP_HEADER_CARD_COLUMN_STYLE} style={{ flexGrow: 1 }}>
          {isRoom && <div css={GROUP_HEADER_CARD_COLUMN_LABEL_STYLE}>LOCATION</div>}
          <LabeledInput
            label='Label'
            value={group.customLabel ?? ''}
            disabled={!scanIsEditable}
            onUpdate={value => {
              scanDataMutators.updateGroup(group.id, {
                customLabel: value ? value : (null as unknown as undefined),
              });
            }}
            tabIndex={1}
          />
        </div>
        {isRoom
          ? (
            <>
              <div css={GROUP_HEADER_CARD_COLUMN_STYLE}>
                <div css={GROUP_HEADER_CARD_COLUMN_LABEL_STYLE}>FLOOR</div>
                <div css={GROUP_HEADER_CARD_SUB_COLUMNS_STYLE}>
                  <div css={GROUP_HEADER_CARD_SUB_COLUMN_STYLE}>
                    <ConditionalScanValueView valueKey='roomLength' group={group} updateValue={updateValue} label='Length' tabIndex={2} />
                    <ConditionalScanValueView valueKey='roomArea' style='calculated' group={group} updateValue={updateValue} label='Area' tabIndex={4} />
                  </div>
                  <div css={GROUP_HEADER_CARD_SUB_COLUMN_STYLE}>
                    <ConditionalScanValueView valueKey='roomWidth' group={group} updateValue={updateValue} label='Width' tabIndex={3} />
                    <ConditionalScanValueView
                      valueKey='roomPerimeter'
                      style='calculated'
                      group={group}
                      updateValue={updateValue}
                      label='Perimeter'
                      tabIndex={5}
                    />
                  </div>
                </div>
              </div>
              <div css={GROUP_HEADER_CARD_COLUMN_STYLE}>
                <div css={GROUP_HEADER_CARD_COLUMN_LABEL_STYLE}>WALLS</div>
                <div css={GROUP_HEADER_CARD_SUB_COLUMN_STYLE}>
                  <ConditionalScanValueView valueKey='roomHeight' group={group} updateValue={updateValue} label='Height' tabIndex={3} />
                  <ConditionalScanValueView valueKey='roomWallArea' style='calculated' group={group} updateValue={updateValue} label='Wall Area' tabIndex={6} />
                </div>
              </div>
            </>
          )
          : null}
      </div>
    </div>
  );
};

export const GroupEditor = ({
  autoOpenRoomMeasurementsGroupId,
  baselineGroup,
  filterRegex,
  group,
  initialExpandedTaskId,
  onAutoOpenRoomMeasurementsConsumed,
  onOpenProjectMeasurements,
}: {
  autoOpenRoomMeasurementsGroupId?: string | null;
  baselineGroup?: ScanGroupOptimized;
  filterRegex?: RegExp;
  group: ScanGroupOptimized;
  initialExpandedTaskId?: string;
  onAutoOpenRoomMeasurementsConsumed?: () => void;
  onOpenProjectMeasurements?: () => void;
}) => {
  const { featureFlags } = useFeatureFlags();
  const { scanRoot, scan, scanDataMutators, scanId, scanHasScopedTasks, scanIsEditable } = useScanEditingContextSlice([
    'scanRoot',
    'scan',
    'scanDataMutators',
    'scanId',
    'scanHasScopedTasks',
    'scanIsEditable',
  ]);
  const [selectedTask, setSelectedTask] = useState<ScanTaskOptimized | null>(null);
  const [roomMeasurementsModalOpen, setRoomMeasurementsModalOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { scanPartNotes } = useScanPartNotes(group.id);

  const scopeOrChangeRequest = scan?.scanType === 'changeRequest' ? 'Change Order' : 'Scope';
  const handlePrintPreview = () => {
    if (!scan) return;
    if (scan.scanType === 'changeRequest') {
      navigate(`/scan/${scan.id}/change-order`);
    } else {
      navigate(`/scan/${scan.id}/scope`);
    }
  };

  useEffect(() => {
    if (!selectedTask) return;
    const updated = group.tasks?.find(t => t.id === selectedTask.id);
    if (updated) setSelectedTask(updated);
  }, [group.id, selectedTask, group.tasks]);

  const otherTaskTemplate = useMemo(() => scanRoot ? getOtherTaskTemplate(scanRoot, group) : undefined, [scanRoot, group]);

  const addOtherTask = async () => {
    if (!otherTaskTemplate || !scanRoot) return;
    const newTask = initTaskFromTemplate(otherTaskTemplate, getScopeForScanPartChildren(group), Date.now());
    scanDataMutators.updateScan(
      getScanWithUpdatedGroup(scanRoot, group.id, {
        tasks: [...(group.tasks ?? []), newTask],
      }),
    );
  };
  const area = getAreaIfDefined(group);
  const isRoom = !!group.template.values?.roomArea;
  const useRoomMeasurements = !!featureFlags.useRoomMeasurements;
  const showMeasurementsTasksHeaderActions = isRoom && useRoomMeasurements && scanIsEditable;

  useEffect(() => {
    if (
      !autoOpenRoomMeasurementsGroupId
      || autoOpenRoomMeasurementsGroupId !== group.id
    ) {
      return;
    }
    if (isRoom && useRoomMeasurements) {
      setRoomMeasurementsModalOpen(true);
    }
    onAutoOpenRoomMeasurementsConsumed?.();
  }, [
    autoOpenRoomMeasurementsGroupId,
    group.id,
    isRoom,
    onAutoOpenRoomMeasurementsConsumed,
    useRoomMeasurements,
  ]);

  const { clusterOrder, taskClusters } = useMemo(() =>
    getTasksInStandardSortOrder(
      group.template,
      group.tasks ?? [],
      filterRegex,
    ), [group.template, group.tasks, filterRegex]);

  if (!scan) return null;
  return (
    <div css={GROUP_EDITOR_STYLE}>
      <PhotoColumn group={group!} />

      <div css={GROUP_STYLE} ref={ref}>
        {isRoom && useRoomMeasurements
          ? (
            <RoomMeasurementsModal
              visible={roomMeasurementsModalOpen}
              group={group}
              onClose={() => setRoomMeasurementsModalOpen(false)}
              onOpenProjectMeasurements={onOpenProjectMeasurements}
              onSave={groupUpdates => scanDataMutators.updateGroup(group.id, groupUpdates).then(() => undefined)}
            />
          )
          : undefined}
        <ScanEditorPageTitleBar
          scanLifecycleStatus={scan.scanLifecycleStatus}
          title={groupNamePresenter(group)}
          area={area}
          cost={getTotalCostIfDefined(group)}
          showArea={isRoom && area != null && !showMeasurementsTasksHeaderActions}
          showCost={!showMeasurementsTasksHeaderActions}
          buttons={compactFlatten([
            !showMeasurementsTasksHeaderActions && (
              <ScanPartNotesButton
                key={group.id + 'notes'}
                alwaysDark={true}
                scanPart={group}
                notes={scanPartNotes}
              />
            ),
            scanHasScopedTasks && (
              <Button
                key='printPreview'
                buttonStyle={'green'}
                onClick={handlePrintPreview}
              >
                {scanIsEditable ? 'PREVIEW' : 'VIEW'} {scopeOrChangeRequest}
              </Button>
            ),
          ])}
          flushButtons={!scanHasScopedTasks}
        />
        <div css={SCROLLABLE_CONTENT_STYLE}>
          {!useRoomMeasurements
            ? (
              <div css={FIXED_HEIGHT_STYLE}>
                <GroupRoomValuesEditor group={group} />
              </div>
            )
            : undefined}
          <div css={TASKS_SECTION_STYLE}>
            {showMeasurementsTasksHeaderActions
              ? (
                <ScanEditorPageMeasurementTitleBar
                  group={group}
                  scanIsEditable={scanIsEditable}
                  costFormatted={formatUsd(getTotalCostIfDefined(group) ?? 0)}
                  onCommitCustomLabel={value =>
                    scanDataMutators.updateGroup(group.id, {
                      customLabel: value.trim() ? value.trim() : (null as unknown as undefined),
                    })}
                  onOpenMeasurementsModal={() => setRoomMeasurementsModalOpen(true)}
                  buttons={compactFlatten([
                    <ScanPartNotesButton
                      key={`${group.id}-tasks-header-notes`}
                      alwaysDark={true}
                      scanPart={group}
                      notes={scanPartNotes}
                      useScanNavigatorNotesIcon={true}
                    />,
                    otherTaskTemplate && scanIsEditable ? <AddButton onClick={addOtherTask} /> : undefined,
                  ])}
                />
              )
              : otherTaskTemplate && scanIsEditable
              ? (
                <div css={TASKS_HEADER_ADD_ONLY_CONTAINER_STYLE}>
                  <div css={TASKS_HEADER_ADD_ONLY_ROW_STYLE}>
                    <AddButton onClick={addOtherTask} />
                  </div>
                </div>
              )
              : null}
            <ClusteredTasksEditor
              baselineTasksById={baselineGroup?.tasksById}
              groupId={group.id}
              clusterOrder={clusterOrder}
              taskClusters={taskClusters}
              stickyTitleTop={LAYOUT2.spacing4}
              initialExpandedTaskId={initialExpandedTaskId}
            />
            <div css={TASK_LIST_BOTTOM_SPACING_STYLE} />
          </div>
        </div>
      </div>
    </div>
  );
};
